/**
 * Public header — client only.
 * Primary: Open Free Desk. Pricing is secondary.
 */

'use client'

import React from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { MenuOutlined } from '@ant-design/icons'

type LandingNavbarProps = {
  variant?: 'home' | 'pricing'
}

const NAV_ANCHORS = [
  { href: '#overview', label: 'Overview' },
  { href: '#workspace', label: 'Workspace' },
  { href: '#surfaces', label: 'How It Works' },
  { href: '#access', label: 'Get Started' },
  { href: '#faq', label: 'FAQ' },
] as const

export const LandingNavbar: React.FC<LandingNavbarProps> = ({ variant = 'home' }) => {
  const anchorHref = (hash: string) => (variant === 'home' ? hash : `/${hash}`)

  return (
    <nav className="landing-navbar" aria-label="Primary">
      <div className="landing-navbar-container">
        <Link href="/" className="landing-navbar-brand" aria-label="Mechagora home">
          <span className="landing-brand-mark" aria-hidden="true">
            <Image
              src="/assets/brand/logo-agent-m.png"
              alt=""
              width={40}
              height={40}
              className="landing-brand-mark-img"
              priority
            />
          </span>
          <span className="landing-brand-text">
            <span className="landing-brand-name" translate="no">
              Mechagora
            </span>
            <span className="landing-brand-sub">Markets · portfolio · agents</span>
          </span>
        </Link>

        <div className="landing-navbar-links" role="navigation" aria-label="Primary links">
          {NAV_ANCHORS.map((item) =>
            variant === 'home' ? (
              <a key={item.href} href={item.href} className="landing-nav-link">
                {item.label}
              </a>
            ) : (
              <Link key={item.href} href={anchorHref(item.href)} className="landing-nav-link">
                {item.label}
              </Link>
            ),
          )}
          <Link
            href="/pricing"
            className={`landing-nav-link${variant === 'pricing' ? ' is-active' : ''}`}
            aria-current={variant === 'pricing' ? 'page' : undefined}
          >
            Pricing
          </Link>
        </div>

        <div className="landing-navbar-actions">
          <Link href="/auth/login" className="landing-btn landing-btn-quiet">
            Sign In
          </Link>
          <Link href="/auth/client-register" className="landing-btn landing-btn-primary">
            Open Free Desk
          </Link>
        </div>

        <details className="landing-nav-menu">
          <summary className="landing-nav-menu-trigger">
            <MenuOutlined aria-hidden="true" />
            <span>Menu</span>
          </summary>
          <div className="landing-nav-menu-panel">
            {NAV_ANCHORS.map((item) =>
              variant === 'home' ? (
                <a key={item.href} href={item.href} className="landing-nav-menu-link">
                  {item.label}
                </a>
              ) : (
                <Link key={item.href} href={anchorHref(item.href)} className="landing-nav-menu-link">
                  {item.label}
                </Link>
              ),
            )}
            <Link href="/pricing" className="landing-nav-menu-link">
              Pricing
            </Link>
            <Link href="/auth/login" className="landing-nav-menu-link">
              Sign In
            </Link>
            <Link href="/auth/client-register" className="landing-nav-menu-cta">
              Open Free Desk
            </Link>
          </div>
        </details>
      </div>
    </nav>
  )
}
